const DB_NAME = "campushubai-offline";
const DB_VERSION = 2;
const API_CACHE_STORE = "api-get-cache";
const OUTBOX_STORE = "mutation-outbox";
const FALLBACK_PREFIX = "campushubai-offline-cache:";
const MAX_CACHE_AGE_MS = 1000 * 60 * 60 * 24 * 14;

export interface OfflineCacheEntry<T = unknown> {
  key: string;
  url: string;
  scope: string;
  data: T;
  savedAt: number;
}

export interface OfflineCacheMeta {
  key: string;
  url: string;
  savedAt: number;
}

const hasIndexedDb = typeof indexedDB !== "undefined";

let dbPromise: Promise<IDBDatabase> | null = null;

function openOfflineDb(): Promise<IDBDatabase> {
  if (!hasIndexedDb) {
    return Promise.reject(new Error("IndexedDB is not available"));
  }

  if (dbPromise) {
    return dbPromise;
  }

  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(API_CACHE_STORE)) {
        db.createObjectStore(API_CACHE_STORE, { keyPath: "key" });
      }
        if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
          db.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
        }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Unable to open offline cache"));
    request.onblocked = () => reject(new Error("Offline cache upgrade was blocked"));
  });

  dbPromise = promise.catch((error: unknown) => {
    dbPromise = null;
    throw error;
  });

  return dbPromise;
}

function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(",")}]`;
  }

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`)
    .join(",")}}`;
}

export function createOfflineCacheKey(args: {
  url: string;
  params?: Record<string, unknown>;
  userId?: string | null;
  schoolId?: string | null;
}): string {
  return stableSerialize({
    url: args.url,
    params: args.params ?? null,
    userId: args.userId ?? "anonymous",
    schoolId: args.schoolId ?? "default",
  });
}

function fallbackStorageKey(key: string): string {
  return `${FALLBACK_PREFIX}${key}`;
}

async function writeIndexedDb<T>(entry: OfflineCacheEntry<T>): Promise<void> {
  const db = await openOfflineDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(API_CACHE_STORE, "readwrite");
    tx.objectStore(API_CACHE_STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("Unable to write offline cache"));
  });
}

async function readIndexedDb<T>(key: string): Promise<OfflineCacheEntry<T> | null> {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(API_CACHE_STORE, "readonly");
    const request = tx.objectStore(API_CACHE_STORE).get(key);
    request.onsuccess = () => resolve((request.result as OfflineCacheEntry<T> | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error("Unable to read offline cache"));
  });
}

function writeFallback<T>(entry: OfflineCacheEntry<T>): void {
  localStorage.setItem(fallbackStorageKey(entry.key), JSON.stringify(entry));
}

function readFallback<T>(key: string): OfflineCacheEntry<T> | null {
  const raw = localStorage.getItem(fallbackStorageKey(key));
  if (!raw) return null;
  return JSON.parse(raw) as OfflineCacheEntry<T>;
}

function isFresh(entry: OfflineCacheEntry): boolean {
  return Date.now() - entry.savedAt <= MAX_CACHE_AGE_MS;
}

export async function writeOfflineCache<T>(entry: OfflineCacheEntry<T>): Promise<void> {
  try {
    await writeIndexedDb(entry);
  } catch {
    try {
      writeFallback(entry);
    } catch {
      // Caching should never break the online request path.
    }
  }
}

export async function readOfflineCache<T>(key: string): Promise<OfflineCacheEntry<T> | null> {
  try {
    const entry = await readIndexedDb<T>(key);
    return entry && isFresh(entry) ? entry : null;
  } catch {
    try {
      const entry = readFallback<T>(key);
      return entry && isFresh(entry) ? entry : null;
    } catch {
      return null;
    }
  }
}

export function notifyOfflineCacheUsed(meta: OfflineCacheMeta): void {
  window.dispatchEvent(new CustomEvent<OfflineCacheMeta>("campushub:offline-cache-used", { detail: meta }));
}

/**
 * Wipe every cached GET response — called on logout (CAMP P1 module audit,
 * shared-device safety). `createOfflineCacheKey` folds `userId` into the key,
 * so a stale entry keyed to the outgoing user is otherwise unreachable by
 * the next login rather than actually gone — on a shared kiosk / front-desk
 * PC / driver phone that is one more thing left behind for whoever logs in
 * next to potentially read (an offline-served fallback pulls from here
 * before ever hitting the network).
 */
export async function clearAllOfflineCache(): Promise<void> {
  try {
    const db = await openOfflineDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(API_CACHE_STORE, "readwrite");
      tx.objectStore(API_CACHE_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Unable to clear offline cache"));
    });
  } catch {
    // IndexedDB unavailable/wedged — fall through to the localStorage sweep
    // below, which is the actual fallback store in that case anyway.
  }
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(FALLBACK_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // Best-effort — a logout must never throw and block the redirect.
  }
}

